/**
 * My Rituality Service
 * API client methods for the My Rituality dashboard
 */

import { apiClient } from "@/lib/api-client"
import type {
  CompletedRitualsResponse,
  CreateGrowthReminderRequest,
  CreateGrowthReminderResponse,
  GrowthRemindersResponse,
  PracticeStatisticsResponse,
} from "../types/my-rituality.types"

/**
 * Get practice statistics for a user
 */
export const getPracticeStatistics = async (
  userId: string,
): Promise<PracticeStatisticsResponse> => {
  const response = await apiClient.get(`/api/profiles/${userId}/practice-statistics`)
  return response.data
}

/**
 * Get growth reminders for the authenticated user
 */
export const getGrowthReminders = async (): Promise<GrowthRemindersResponse> => {
  const response = await apiClient.get("/api/growth-reminders")
  return response.data
}

/**
 * Create a custom growth reminder.
 * Sends as multipart FormData when an image file is provided.
 */
export const createGrowthReminder = async (
  data: CreateGrowthReminderRequest,
): Promise<CreateGrowthReminderResponse> => {
  if (data.imageFile) {
    const formData = new FormData()
    formData.append("text", data.text)
    formData.append("image", data.imageFile)
    const response = await apiClient.post("/api/growth-reminders", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    return response.data
  }
  const response = await apiClient.post("/api/growth-reminders", {
    text: data.text,
    backgroundImageKey: data.backgroundImageKey,
  })
  return response.data
}

/**
 * Delete a growth reminder
 */
export const deleteGrowthReminder = async (reminderId: string): Promise<void> => {
  await apiClient.delete(`/api/growth-reminders/${reminderId}`)
}

/**
 * Update a growth reminder
 */
export const updateGrowthReminder = async (
  reminderId: string,
  data: { text?: string; backgroundImageKey?: string },
): Promise<void> => {
  await apiClient.patch(`/api/growth-reminders/${reminderId}`, data)
}

/**
 * Get completed rituals for a user
 */
export const getCompletedRituals = async (
  userId: string,
  params?: { page?: number; limit?: number },
): Promise<CompletedRitualsResponse> => {
  const response = await apiClient.get(`/api/rituals/user/${userId}/completed`, { params })
  return response.data
}
